import { useEffect, useRef, useState } from "react";
import { api } from "../api";

const EMPTY = {
  percent: 0,
  message: "",
  status: "",
  phase: "",
  elapsed: null,
  eta: null,
  done: false,
  error_message: null,
};

/** Percent usable para la barra: prioriza API, luego % en el mensaje del worker. */
export function resolveConvertPercent(info, { status = "" } = {}) {
  let pct = Number(info?.percent);
  if (!Number.isFinite(pct)) pct = 0;
  if (pct <= 0) {
    const m = String(info?.message || "").match(/(\d{1,3})\s*%/);
    if (m) pct = Number(m[1]);
  }
  if (pct <= 0 && (info?.phase === "ffmpeg" || status === "processing")) {
    pct = 5;
  }
  if (pct <= 0 && status === "queued") {
    pct = 1;
  }
  return Math.max(0, Math.min(100, Math.round(pct)));
}

/**
 * Poll del progreso real de conversión (Redis/API).
 * El tiempo `elapsed` viene del started_at del worker, no del reloj local.
 */
export default function useConvertProgress(token, videoId, enabled = true) {
  const [info, setInfo] = useState(EMPTY);
  const [tick, setTick] = useState(0);
  const lastPercentRef = useRef(0);

  useEffect(() => {
    if (!token || !videoId || !enabled) {
      setInfo(EMPTY);
      lastPercentRef.current = 0;
      return undefined;
    }
    let cancelled = false;
    let timer;

    async function poll() {
      try {
        const data = await api(`/api/videos/${videoId}/convert-progress`, { token });
        if (cancelled) return;
        const pct = resolveConvertPercent(data, { status: data.status });
        lastPercentRef.current = pct;
        setInfo({ ...data, percent: pct });
        if (data.done) return;
      } catch (_) {
        /* conservar último percent conocido */
      }
      if (!cancelled) timer = window.setTimeout(poll, 900);
    }

    poll();
    const clock = window.setInterval(() => setTick((n) => n + 1), 500);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.clearInterval(clock);
    };
  }, [token, videoId, enabled]);

  let elapsed = info.elapsed;
  if (info.started_at && !info.done) {
    elapsed = Math.max(0, Date.now() / 1000 - Number(info.started_at));
  }
  void tick;

  const percent =
    resolveConvertPercent(info, { status: info.status }) ||
    lastPercentRef.current ||
    0;

  return { ...info, percent, elapsed };
}

export function formatDuration(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return "—";
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}
